// Add Injury modal — Mockup Spec §3.5 layout. React Hook Form + Zod-shaped
// fields. Auto-saves draft to localStorage on every keystroke (Loop 2G gate:
// browser refresh mid-flow restores draft). File upload UI mocked; real R2
// upload in Stage 6.

import { useEffect, useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { Paperclip, X } from 'lucide-react';
import { useCreateInjury, useUpdateInjury, type InjuryWithPlayer } from '@/hooks/useInjuries';
import { usePlayers, type PlayerListItem } from '@/hooks/usePlayers';

type FormValues = {
  playerId: string;
  injuryType: string;
  severity: string;
  injuryDate: string;
  gameContext: string;
  doctor: string;
  projectedReturnDate: string;
  notes: string;
  status: 'active' | 'archived';
};

const DRAFT_KEY = 'faceoff:add-injury-draft:v1';

function todayISO() {
  const now = new Date();
  return now.toISOString().slice(0, 10);
}

function emptyDraft(): FormValues {
  return {
    playerId: '',
    injuryType: '',
    severity: '',
    injuryDate: todayISO(),
    gameContext: '',
    doctor: '',
    projectedReturnDate: '',
    notes: '',
    status: 'active',
  };
}

export function AddInjuryModal({
  onClose,
  defaultPlayerId,
  injury,
}: {
  onClose: () => void;
  defaultPlayerId?: string;
  injury?: InjuryWithPlayer;
}) {
  const [restored, setRestored] = useState(false);
  const isEditing = Boolean(injury);
  const initial = useMemo<FormValues>(() => {
    if (injury) {
      return {
        playerId: injury.playerId,
        injuryType: injury.injuryType,
        severity: injury.severity ?? '',
        injuryDate: injury.injuryDate,
        gameContext: injury.gameContext ?? '',
        doctor: injury.doctor ?? '',
        projectedReturnDate: injury.projectedReturnDate ?? '',
        notes: injury.notes ?? '',
        status: injury.status,
      };
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as FormValues;
        return { ...emptyDraft(), ...parsed, playerId: defaultPlayerId ?? parsed.playerId };
      }
    } catch {
      /* ignore */
    }
    return { ...emptyDraft(), playerId: defaultPlayerId ?? '' };
  }, [defaultPlayerId, injury]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: initial });

  // Restore-banner shows once if we loaded a draft.
  useEffect(() => {
    if (isEditing) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw && !defaultPlayerId) setRestored(true);
    } catch {
      /* ignore */
    }
  }, [defaultPlayerId, isEditing]);

  // Autosave new injury drafts only. Existing injury edits should not overwrite
  // the next add-injury draft.
  const watched = watch();
  useEffect(() => {
    if (isEditing) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(watched));
    } catch {
      /* ignore */
    }
  }, [isEditing, watched]);

  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const create = useCreateInjury();
  const update = useUpdateInjury();
  const players = usePlayers({});

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const payload = {
      playerId: values.playerId,
      injuryType: values.injuryType,
      severity: values.severity || null,
      injuryDate: values.injuryDate,
      gameContext: values.gameContext || null,
      doctor: values.doctor || null,
      projectedReturnDate: values.projectedReturnDate || null,
      notes: values.notes || null,
      status: values.status,
    };
    if (injury) {
      await update.mutateAsync({
        id: injury.id,
        patch: {
          injuryType: payload.injuryType,
          severity: payload.severity,
          injuryDate: payload.injuryDate,
          gameContext: payload.gameContext,
          doctor: payload.doctor,
          projectedReturnDate: payload.projectedReturnDate,
          notes: payload.notes,
        },
      });
    } else {
      await create.mutateAsync(payload);
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* ignore */
      }
    }
    onClose();
  };

  function clearDraft() {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
    Object.entries(emptyDraft()).forEach(([k, v]) => setValue(k as keyof FormValues, v));
    setRestored(false);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-injury-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/50 p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-xl bg-white shadow-xl sm:rounded-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 id="add-injury-title" className="text-[16px] font-semibold">
            {isEditing ? 'Edit Injury' : 'Add Injury'}
          </h2>
          <button onClick={onClose} aria-label="Close" className="text-muted hover:text-ink">
            <X size={18} />
          </button>
        </div>

        {restored && (
          <div className="flex items-center justify-between gap-2 border-b border-border bg-bartlett/5 px-5 py-2 text-[12px]">
            <span className="text-muted">Restored unsaved draft</span>
            <button onClick={clearDraft} className="font-mono text-bartlett hover:underline">
              start fresh
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
          <div className="space-y-4">
            <Field label="Player" required error={errors.playerId?.message}>
              <select
                {...register('playerId', { required: 'Pick a player' })}
                disabled={isEditing}
                className="h-10 w-full rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett"
              >
                <option value="">- select -</option>
                {(players.data?.items ?? []).map((p: PlayerListItem) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName ?? `${p.firstName} ${p.lastName}`}
                    {p.currentTeam ? ` · ${p.currentTeam}` : ''}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Injury type" required error={errors.injuryType?.message}>
              <input
                {...register('injuryType', { required: 'Required' })}
                placeholder='e.g. "right shoulder labrum"'
                className="h-10 w-full rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett"
              />
            </Field>

            <Field label="Severity (optional)">
              <input
                {...register('severity')}
                placeholder='e.g. "Grade 2 AC joint"'
                className="h-10 w-full rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Injury date" required error={errors.injuryDate?.message}>
                <input
                  type="date"
                  {...register('injuryDate', { required: 'Required' })}
                  className="h-10 w-full rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett"
                />
              </Field>
              <Field label="Projected return (optional)">
                <input
                  type="date"
                  {...register('projectedReturnDate')}
                  className="h-10 w-full rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett"
                />
              </Field>
            </div>

            <Field label="Game / context (optional)">
              <input
                {...register('gameContext')}
                placeholder='e.g. "vs CHI, 1st period"'
                className="h-10 w-full rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett"
              />
            </Field>

            <Field label="Doctor (optional)">
              <input
                {...register('doctor')}
                placeholder='e.g. "Dr. Smith @ HSS"'
                className="h-10 w-full rounded-control border border-border bg-white px-3 text-sm outline-none focus:border-bartlett"
              />
            </Field>

            <Field label="Notes (optional)">
              <textarea
                {...register('notes')}
                rows={3}
                placeholder="Running narrative — PT, milestones, return targets…"
                className="w-full rounded-control border border-border bg-white p-3 text-sm outline-none focus:border-bartlett"
              />
            </Field>

            <Field label="Attachments">
              <div
                className="inline-flex h-9 items-center gap-1.5 rounded-control border border-dashed border-border px-3 text-[12px] text-muted"
              >
                <Paperclip size={13} />
                Upload PDFs from the injury card
              </div>
            </Field>

            {!isEditing && (
              <Field label="Status">
                <div className="flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="radio" value="active" {...register('status')} />
                    <span>Active</span>
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="radio" value="archived" {...register('status')} />
                    <span>Archived</span>
                  </label>
                </div>
              </Field>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between gap-2">
            {isEditing ? (
              <span className="font-mono text-[11px] text-muted">updates existing IR record</span>
            ) : (
              <button
                type="button"
                onClick={clearDraft}
                className="font-mono text-[11px] text-muted hover:text-ink"
              >
                clear draft
              </button>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-10 rounded-control border border-border bg-white px-4 text-sm font-semibold text-ink hover:bg-bg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || create.isPending || update.isPending}
                className="h-10 rounded-control border border-bartlett bg-bartlett px-4 text-sm font-semibold text-white hover:bg-bartlett/95 disabled:opacity-50"
              >
                {isSubmitting || create.isPending || update.isPending ? 'Saving...' : isEditing ? 'Save changes' : 'Save'}
              </button>
            </div>
          </div>

          {(create.isError || update.isError) && (
            <p className="mt-2 text-[12px] text-flag-critical">
              {create.error instanceof Error
                ? create.error.message
                : update.error instanceof Error
                  ? update.error.message
                  : 'Save failed'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted">
        {label}
        {required && <span className="ml-1 text-flag-critical">*</span>}
      </span>
      {children}
      {error && <span className="text-[11px] text-flag-critical">{error}</span>}
    </label>
  );
}
